import React, { useState, useEffect } from 'react';
import Card from 'components/card';
import { FaCheckCircle, FaTimesCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import AxiosInstance from '../../../../axiosConfig';

const GlobalConfigs = () => {
  const [activeTab, setActiveTab] = useState('configs');
  const [globalParameters, setGlobalParameters] = useState([]);
  const [channelSettings, setChannelSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState({});
  const [error, setError] = useState(null);
  const [newParameter, setNewParameter] = useState({
    info_type: '',
    short_description: '',
    description: '',
    is_encrypted: false,
  });
  // State to manage which sections are expanded
  const [expandedSections, setExpandedSections] = useState({});
  // State for confirmation modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const fetchGlobalParameters = () => {
    setLoading(true);
    AxiosInstance.get('/get-global-pars')
      .then(response => {
        if (response.data.status === 'success') {
          setGlobalParameters(response.data.message);
          setError(null);
        } else {
          setError(response.data.message || 'Failed to fetch global parameters.');
        }
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Error fetching global parameters from backend.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchChannelSettings = () => {
    setLoading(true);
    AxiosInstance.get('/all-channel-settings')
      .then(response => {
        console.log('Channel Settings Response:', response.data.message); // Debug API response
        if (response.data.status === 'success') {
          setChannelSettings(response.data.message);
          setError(null);
        } else {
          setError(response.data.message || 'Failed to fetch channel settings.');
        }
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Error fetching channel settings from backend.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (activeTab === 'parameters') {
      fetchGlobalParameters();
    }
    if (activeTab === 'configs') {
      fetchChannelSettings();
    }
  }, [activeTab]);

  const handleNewParameterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewParameter(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveNewParameter = async (e) => {
    e.preventDefault();
    if (!newParameter.info_type || !newParameter.short_description || !newParameter.description) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      const response = await AxiosInstance.post('/save-global-par', {
        ...newParameter,
        entry_status: true,
      });

      if (response.data.status === 'success') {
        setNewParameter({ info_type: '', short_description: '', description: '', is_encrypted: false });
        setError(null);
        setActiveTab('parameters');
      } else {
        setError(response.data.message || 'Failed to save new parameter.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving new parameter.');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (settingKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [settingKey]: !prev[settingKey]
    }));
  };

  const initiateSetAll = (key, value) => {
    setPendingAction({ key, value });
    setShowConfirm(true);
  };

  const confirmSetAll = async () => {
    setShowConfirm(false);
    if (!pendingAction) return;

    const { key, value } = pendingAction;
    setLoadingSettings(prev => ({ ...prev, [key]: true }));
    try {
      const apiValue = value ? 'True' : 'False';
      const response = await AxiosInstance.get(`/global-switch?parameter=${key}&value=${apiValue}`);
      if (response.data.status === 'success') {
        fetchChannelSettings();
      } else {
        setError(response.data.message || 'Failed to update setting.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating setting.');
    } finally {
      setLoadingSettings(prev => ({ ...prev, [key]: false }));
      setPendingAction(null);
    }
  };

  const cancelSetAll = () => {
    setShowConfirm(false);
    setPendingAction(null);
  };

  const renderConfigOptions = () => {
    if (loading) {
      return <p>Loading channel settings...</p>;
    }
    if (error) {
      return <p className="text-red-500">{error}</p>;
    }
    if (!channelSettings) {
      return <p>No channel settings found.</p>;
    }

    const configLabels = {
      translation: 'Allow multi-lingua translation',
      gen_ai_enable: 'Activate GenAI Capability',
      privacy_terms: 'Enforce Terms and Conditions Acceptance',
      channel_status: 'Channels Status',
      human_agents_on: 'Escalate to Human Agents',
      anti_spam_on: 'Block Spamming'
    };

    // Ensure all configLabels keys are processed
    return (
      <div className="flex flex-col space-y-6">
        {Object.keys(configLabels).map((key) => {
          const channels = channelSettings[key];
          let settingType;
          let allOn;
          if (channels === undefined) {
            settingType = 'missing';
          } else if (typeof channels === 'boolean') {
            settingType = 'global';
            allOn = channels;
          } else if (typeof channels === 'object') {
            const channelCount = Object.keys(channels).length;
            if (channelCount > 0) {
              settingType = 'perChannel';
              allOn = Object.values(channels).every(status => status);
            } else {
              settingType = 'noChannels';
            }
          }

          const label = settingType === 'perChannel' ? 'All Channels' : settingType === 'global' ? 'Global' : settingType === 'noChannels' ? 'No Channels' : 'Not Configured';

          return (
            <div key={key} className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 cursor-pointer" onClick={() => toggleExpanded(key)}>
                  {expandedSections[key] ? (
                    <FaChevronUp className="text-gray-500 dark:text-gray-400" />
                  ) : (
                    <FaChevronDown className="text-gray-500 dark:text-gray-400" />
                  )}
                  <h5 className="font-bold text-lg text-navy-700 dark:text-white">{configLabels[key]}</h5>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
                  {settingType !== 'noChannels' && settingType !== 'missing' && (
                    allOn ? (
                      <FaCheckCircle className="text-green-500" />
                    ) : (
                      <FaTimesCircle className="text-red-500" />
                    )
                  )}
                </div>
              </div>
              
              {expandedSections[key] && (
                <div className="mt-4">
                  {settingType !== 'noChannels' && settingType !== 'missing' && (
                    <div className="flex gap-2 mb-4 ml-4">
                      <button
                        onClick={() => initiateSetAll(key, true)}
                        disabled={loadingSettings[key]}
                        className={`px-3 py-1 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600 disabled:bg-green-300 disabled:cursor-not-allowed transition duration-300 ease-in-out shadow-sm hover:shadow-md ${loadingSettings[key] ? 'animate-pulse' : ''}`}
                      >
                        {loadingSettings[key] ? 'Updating...' : 'Set ON'}
                      </button>
                      <button
                        onClick={() => initiateSetAll(key, false)}
                        disabled={loadingSettings[key]}
                        className={`px-3 py-1 bg-red-800 text-white rounded-md text-sm font-medium hover:bg-red-900 disabled:bg-red-500 disabled:cursor-not-allowed transition duration-300 ease-in-out shadow-sm hover:shadow-md ${loadingSettings[key] ? 'animate-pulse' : ''}`}
                      >
                        {loadingSettings[key] ? 'Updating...' : 'Set OFF'}
                      </button>
                    </div>
                  )}
                  {settingType === 'global' ? (
                    <div className="flex items-center gap-3 ml-4">
                      {channels ? (
                        <FaCheckCircle className="text-green-500" />
                      ) : (
                        <FaTimesCircle className="text-red-500" />
                      )}
                      <span className="text-md font-medium text-navy-700 dark:text-white">
                        Global setting
                      </span>
                    </div>
                  ) : settingType === 'perChannel' ? (
                    <div className="flex flex-wrap gap-4 ml-4">
                      {Object.entries(channels).map(([channel, status]) => (
                        <div key={channel} className="flex items-center gap-2">
                          {status ? (
                            <FaCheckCircle className="text-green-500" />
                          ) : (
                            <FaTimesCircle className="text-red-500" />
                          )}
                          <span className="text-sm font-medium capitalize text-gray-700 dark:text-gray-300">
                            {channel}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="ml-4 text-sm text-gray-500 dark:text-gray-400">
                      {settingType === 'noChannels' ? 'No channels configured for this setting.' : 'This setting is not yet configured.'}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderConfirmModal = () => {
    if (!showConfirm) return null;
    const actionName = pendingAction?.value ? 'Set All ON' : 'Set All OFF';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white mb-4">
            Confirm Action
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Are you sure you want to {actionName}? This global setting will be applied to all channels.
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={cancelSetAll}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
            >
              Cancel
            </button>
            <button
              onClick={confirmSetAll}
              className="px-4 py-2 bg-brand-500 text-white rounded-md hover:bg-brand-600 transition duration-300"
            >
              I'm Sure
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card extra={"w-full h-full p-6 mt-3"}>
      <div className="relative mb-3 pt-1">
        <h4 className="text-3xl font-bold text-navy-700 dark:text-white">
          Global Configs
        </h4>
      </div>

      <div className="flex space-x-2 border-b-2 border-gray-200 dark:border-gray-700 mb-6" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'configs'}
          className={`py-2 px-4 text-base font-medium rounded-t-lg transition-all duration-300
            ${activeTab === 'configs' ? 'bg-brand-500 text-white shadow-md' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          onClick={() => setActiveTab('configs')}
        >
          Config Options
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'parameters'}
          className={`py-2 px-4 text-base font-medium rounded-t-lg transition-all duration-300
            ${activeTab === 'parameters' ? 'bg-brand-500 text-white shadow-md' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          onClick={() => setActiveTab('parameters')}
        >
          Parameters Maintenance
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'add'}
          className={`py-2 px-4 text-base font-medium rounded-t-lg transition-all duration-300
            ${activeTab === 'add' ? 'bg-brand-500 text-white shadow-md' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
          onClick={() => setActiveTab('add')}
        >
          Add New Parameter
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'configs' && (
          <div className="flex flex-col space-y-4" role="tabpanel">
            {renderConfigOptions()}
          </div>
        )}

        {activeTab === 'parameters' && (
          <div className="overflow-x-auto p-3" role="tabpanel">
            {loading && <p>Loading parameters...</p>}
            {error && <p className="text-red-500">{error}</p>}
            {!loading && !error && globalParameters.length > 0 && (
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="py-2 px-4 text-sm font-bold text-gray-400">Info Type</th>
                    <th className="py-2 px-4 text-sm font-bold text-gray-400">Short Description</th>
                    <th className="py-2 px-4 text-sm font-bold text-gray-400">Description</th>
                    <th className="py-2 px-4 text-sm font-bold text-gray-400">Created By</th>
                    <th className="py-2 px-4 text-sm font-bold text-gray-400">Encrypted</th>
                    <th className="py-2 px-4 text-sm font-bold text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {globalParameters.map((param, index) => (
                    <tr key={index} className="border-b border-gray-800">
                      <td className="py-2 px-4">{param.info_type}</td>
                      <td className="py-2 px-4">{param.short_description}</td>
                      <td className="py-2 px-4">{param.description}</td>
                      <td className="py-2 px-4">{param.created_by}</td>
                      <td className="py-2 px-4">{param.is_encrypted ? 'Yes' : 'No'}</td>
                      <td className="py-2 px-4 text-center">
                        {param.entry_status ? (
                          <FaCheckCircle className="text-green-500 mx-auto" />
                        ) : (
                          <FaTimesCircle className="text-red-500 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!loading && !error && globalParameters.length === 0 && (
              <p>No global parameters found.</p>
            )}
          </div>
        )}

        {activeTab === 'add' && (
          <div className="p-3" role="tabpanel">
            {error && <p className="text-red-500 mb-4">{error}</p>}
            <form onSubmit={handleSaveNewParameter} className="space-y-4">
              <div>
                <label htmlFor="info_type" className="block text-sm font-medium text-gray-400">Info Type</label>
                <input
                  type="text"
                  name="info_type"
                  id="info_type"
                  value={newParameter.info_type}
                  onChange={handleNewParameterChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 border-gray-700 text-white shadow-sm focus:border-brand-500 focus:ring-brand-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="short_description" className="block text-sm font-medium text-gray-400">Short Description</label>
                <input
                  type="text"
                  name="short_description"
                  id="short_description"
                  value={newParameter.short_description}
                  onChange={handleNewParameterChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 border-gray-700 text-white shadow-sm focus:border-brand-500 focus:ring-brand-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-400">Description</label>
                <textarea
                  name="description"
                  id="description"
                  rows="3"
                  value={newParameter.description}
                  onChange={handleNewParameterChange}
                  className="mt-1 block w-full rounded-md bg-gray-800 border-gray-700 text-white shadow-sm focus:border-brand-500 focus:ring-brand-500"
                  required
                ></textarea>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_encrypted"
                  id="is_encrypted"
                  checked={newParameter.is_encrypted}
                  onChange={handleNewParameterChange}
                  className="h-4 w-4 text-brand-600 bg-gray-800 border-gray-700 rounded focus:ring-brand-500"
                />
                <label htmlFor="is_encrypted" className="ml-2 block text-sm text-gray-400">Is Encrypted?</label>
              </div>
              <button
                type="submit"
                className={`py-2 px-4 rounded-md font-medium transition-all duration-300
                  ${loading ? 'bg-gray-600 cursor-not-allowed' : 'bg-brand-500 text-white hover:bg-brand-600'}`}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Parameter'}
              </button>
            </form>
          </div>
        )}
      </div>
      {renderConfirmModal()}
    </Card>
  );
};

export default GlobalConfigs;